import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  Box,
  Flex,
  Typography,
  useDesktopMediaQuery,
} from '@iheartjane/reefer';
import { formatCurrency, pluralize } from '@iheartjane/utils';
import { Fragment } from 'react';

import { CheckoutReviewProductCard } from '@jane/shared-ecomm/components';
import { useCheckout } from '@jane/shared-ecomm/providers';
// eslint-disable-next-line no-restricted-imports
import type { PendingCartProduct } from '@jane/shared/models';
import {
  deserializeCannabinoids,
  getOriginalAndDiscountedTotalPrices,
} from '@jane/shared/util';

import { CheckoutStep } from '../checkout.styles';
import { CheckoutButton } from '../CheckoutButton/CheckoutButton';
import { RewardsGrid } from '../CheckoutSummary/checkoutSummary.styles';

const sortAndDeserialize = (products: PendingCartProduct[]) =>
  products
    .map((product) => deserializeCannabinoids(product, 'menuProduct'))
    .sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()));

export const CheckoutReview = () => {
  const isDesktop = useDesktopMediaQuery({ size: 'sm', width: 'min' });

  const {
    appInfo: { bloomUserExperience },
    cart: { crm_redemptions, products },
    cartLineItems: { total },
    store,
  } = useCheckout();
  const showCrmLineItem = useFlag(FLAGS.c3RewardsLineItem);
  // Context cart is v3, but this review's sort/product-card still speak v1
  // PendingCartProduct. The cast is safe: it reads no field that diverges between
  // the shapes (effects/flavors). TODO(carts-v3): drop when readers move to v3.
  const cartProducts = products as unknown as PendingCartProduct[];

  return (
    <CheckoutStep width="100%">
      <Typography variant="header-bold" branded>
        Review your items
      </Typography>

      <Flex flexDirection="column" gap={24} mt={24}>
        {sortAndDeserialize(cartProducts).map((product) => {
          const { originalPrice, discountedPrice } =
            getOriginalAndDiscountedTotalPrices({
              menuProduct: product,
              count: product.count,
              priceId: product.price_id,
            });
          return (
            <CheckoutReviewProductCard
              key={`${product.id}-${product.price_id}`}
              bloomUserExperience={bloomUserExperience}
              store={store}
              product={product as PendingCartProduct}
              originalPrice={originalPrice}
              discountedPrice={discountedPrice}
            />
          );
        })}
      </Flex>

      {isDesktop && (
        <Box>
          <Flex
            justifyContent="space-between"
            pb={showCrmLineItem ? 0 : 24}
            pt={24}
          >
            <Typography variant="header-bold" as="p" branded>
              Estimated total
            </Typography>
            <Typography variant="header-bold" as="p" branded>
              {formatCurrency(total)}
            </Typography>
          </Flex>

          {showCrmLineItem && (
            <Flex pb={24}>
              <RewardsGrid>
                {crm_redemptions?.map((crm_item) => {
                  return (
                    <Fragment key={crm_item.crm_reward_id}>
                      <Typography>Reward: {crm_item.reward_name}</Typography>
                      <Typography textAlign="right">
                        {crm_item.point_value}{' '}
                        {pluralize({
                          noun: 'point',
                          number: crm_item.point_value,
                          returnValue: 'word',
                        })}
                      </Typography>
                    </Fragment>
                  );
                })}
              </RewardsGrid>
            </Flex>
          )}

          <CheckoutButton />
        </Box>
      )}
    </CheckoutStep>
  );
};
